import { useSyncExternalStore } from 'react';

/*
 * Farbe und Hell/Dunkel -- beides eine Einstellung dieses Browsers, nicht des
 * Events. Wer Blau waehlt, sieht die ganze App in Blau, auch Regie und
 * Zuschauersicht; alle anderen sehen weiter ihre eigene Wahl.
 *
 * Die Farbe greift ueber CSS-Variablen (`--brand-*` in styles.css). Rot ist
 * der Ausgangszustand und steht dort fest -- fuer jede andere Farbe setzt
 * `applyBrand` die Variablen am <html>-Element. Bewusst *nicht* betroffen:
 * die Einblendungen im Stream (die sieht jeder Zuschauer und der RTMP-Ausgang
 * gleich) und Warnfarben wie Fehlermeldungen -- die nutzen `--red-*`.
 *
 * Hell/Dunkel gilt ebenfalls ueberall: auf den hellen Seiten ueber die
 * Papier-Farben, auf Regie, Zuschauersicht und Senden ueber die
 * Buehnenfarben. Das Videobild selbst bleibt in beiden Modi dunkel.
 */

export const DEFAULT_BRAND = '#e11d2e';

export interface BrandPreset {
  id: string;
  label: string;
  hex: string;
}

/** Alle so dunkel, dass weisse Schrift darauf mindestens 4,5:1 erreicht. */
export const BRAND_PRESETS: BrandPreset[] = [
  { id: 'red', label: 'Rot', hex: DEFAULT_BRAND },
  { id: 'blue', label: 'Blau', hex: '#2563eb' },
  { id: 'green', label: 'Grün', hex: '#15803d' },
  { id: 'orange', label: 'Orange', hex: '#c2410c' },
  { id: 'purple', label: 'Lila', hex: '#7c3aed' },
  { id: 'teal', label: 'Türkis', hex: '#0f766e' },
];

export type Theme = 'light' | 'dark';

export interface Appearance {
  /** Gewaehlte Farbe als `#rrggbb`, klein geschrieben. */
  brand: string;
  /** Id der Vorgabe oder `custom` fuer eine frei gewaehlte Farbe. */
  brandId: string;
  theme: Theme;
}

const BRAND_KEY = 'rabbitfuel:brand';
const THEME_KEY = 'rabbitfuel:theme';
/** Die Buehnenseiten faerben die Browserleiste wie ihre eigene Kopfleiste. */
const STAGE_THEME_COLOR = { dark: '#0b0c0e', light: '#ffffff' } as const;

/* ---------------------------------------------------------- Farbrechnung */

type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
const INK: Rgb = [16, 18, 21]; // --ink-900
const STAGE_800: Rgb = [20, 22, 26];
const STAGE_900: Rgb = [11, 12, 14];

function parseHex(value: string | null | undefined): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((value ?? '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(c: Rgb): string {
  return `#${c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** `share` Anteile von `a`, der Rest von `b` -- wie `color-mix(in srgb)`. */
function mix(a: Rgb, b: Rgb, share: number): Rgb {
  return [0, 1, 2].map((i) => a[i] * share + b[i] * (1 - share)) as Rgb;
}

function luminance(c: Rgb): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Mischt `c` schrittweise in Richtung `toward`, bis es sich von `against` genug abhebt. */
function reach(c: Rgb, toward: Rgb, against: Rgb, ratio: number): Rgb {
  for (let share = 1; share > 0; share -= 0.04) {
    const next = mix(c, toward, share);
    if (contrast(next, against) >= ratio) return next;
  }
  return toward;
}

/**
 * Leitet aus einer Farbe alle Stufen ab, die styles.css braucht. Die Stufe 600
 * ist die gewaehlte Farbe selbst; Schriftfarben werden so weit aufgehellt oder
 * abgedunkelt, bis sie auf ihrem Grund lesbar sind -- auch bei einer frei
 * gewaehlten, sehr hellen oder sehr dunklen Farbe.
 */
export function brandVars(hex: string): Record<string, string> {
  const c = parseHex(hex) ?? parseHex(DEFAULT_BRAND)!;
  const ink = reach(c, BLACK, WHITE, 4.5);
  return {
    '--brand-50': toHex(mix(c, WHITE, 0.06)),
    '--brand-100': toHex(mix(c, WHITE, 0.12)),
    '--brand-200': toHex(mix(c, WHITE, 0.25)),
    '--brand-500': toHex(mix(c, WHITE, 0.85)),
    '--brand-600': toHex(c),
    '--brand-700': toHex(mix(c, BLACK, 0.82)),
    '--brand-800': toHex(mix(c, BLACK, 0.64)),
    '--brand-on': contrast(c, WHITE) >= contrast(c, INK) ? '#ffffff' : toHex(INK),
    '--brand-ink': toHex(ink),
    '--brand-ink-strong': toHex(mix(ink, BLACK, 0.8)),
    '--brand-glow': toHex(reach(mix(c, WHITE, 0.85), WHITE, STAGE_800, 4)),
    '--brand-bright': toHex(reach(mix(c, WHITE, 0.55), WHITE, STAGE_900, 6)),
    '--brand-light': toHex(reach(mix(c, WHITE, 0.35), WHITE, STAGE_900, 9)),
  };
}

/**
 * Farbe einer einzelnen Einblendung im Stream, als CSS-Variablen fuer deren
 * Kaestchen (siehe `.stream-overlay` in styles.css). Das Kaestchen bleibt
 * dunkel getoent -- deshalb werden die hellen Stufen gegen dunklen Grund
 * geprueft. Ohne Farbe gibt es keine Variablen: dann steht die Einblendung im
 * Standard-Look da, dunkel mit roten Akzenten.
 */
export function overlayAccentVars(hex: string | undefined): Record<string, string> | undefined {
  const c = parseHex(hex);
  if (!c) return undefined;
  const vars = brandVars(toHex(c));
  return {
    '--so-accent': vars['--brand-600'],
    '--so-accent-on': vars['--brand-on'],
    '--so-accent-text': vars['--brand-glow'],
    '--so-accent-light': vars['--brand-light'],
    '--so-accent-pale': toHex(reach(mix(c, WHITE, 0.2), WHITE, STAGE_900, 12)),
  };
}

/**
 * Vereinsfarbe eines Events fuer Karte und Kopf der Eventseite. Ein Rotton ist
 * keine bewusst gewaehlte Vereinsfarbe, sondern unser Standard -- neue Events
 * bekommen #E11D2E, die Demo-Events Abstufungen davon. Der folgt deshalb der
 * Hausfarbe dieses Browsers, in derselben Abstufung. Eine echte eigene Farbe
 * bleibt, wie sie ist. Bild-Hintergrund und Standbild im Stream nutzen die
 * Vereinsfarbe weiter unveraendert -- die sieht jeder Zuschauer gleich.
 */
export function eventAccent(accent: string, brand: string): string {
  const c = parseHex(accent);
  if (!c || brand === DEFAULT_BRAND) return accent;
  const [r, g, b] = c;
  const reddish = r > 100 && g < r * 0.3 && b < r * 0.35;
  if (!reddish) return accent;
  const share = Math.round(Math.min(1, r / 225) * 100);
  return share >= 99 ? 'var(--brand-600)' : `color-mix(in srgb, var(--brand-600) ${share}%, black)`;
}

/* ------------------------------------------------------ Lesen und Setzen */

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Privater Modus o. Ae. -- dann gilt die Wahl nur bis zum Neuladen. */
  }
}

const darkQuery = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function readAppearance(): Appearance {
  const brand = toHex(parseHex(readStorage(BRAND_KEY)) ?? parseHex(DEFAULT_BRAND)!);
  const stored = readStorage(THEME_KEY);
  // Ohne eigene Wahl gilt die Einstellung des Geraets.
  const theme: Theme = stored === 'light' || stored === 'dark' ? stored : darkQuery()?.matches ? 'dark' : 'light';
  return { brand, brandId: BRAND_PRESETS.find((p) => p.hex === brand)?.id ?? 'custom', theme };
}

let current: Appearance = typeof window === 'undefined'
  ? { brand: DEFAULT_BRAND, brandId: 'red', theme: 'light' }
  : readAppearance();
const listeners = new Set<() => void>();

function brandIcon(hex: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' rx='8' fill='${hex}'/><circle cx='16' cy='16' r='6' fill='white'/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Farbe der Browserleiste: auf Buehnenseiten die ihrer Kopfleiste, sonst die
 * gewaehlte Farbe. Wird auch von `useStageSurface` gerufen, wenn sich die
 * Seite wechselt.
 */
export function syncThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  const stage = document.documentElement.dataset.surface === 'stage';
  meta?.setAttribute('content', stage ? STAGE_THEME_COLOR[current.theme] : current.brand);
}

function applyBrand(hex: string) {
  const root = document.documentElement;
  const vars = brandVars(DEFAULT_BRAND);
  // Rot steht als Ausgangswert im Stylesheet -- dann bleibt <html> ohne Angaben.
  const own = hex === DEFAULT_BRAND ? null : brandVars(hex);
  for (const name of Object.keys(vars)) {
    if (own) root.style.setProperty(name, own[name]);
    else root.style.removeProperty(name);
  }
  document.querySelector('link[rel="icon"]')?.setAttribute('href', brandIcon(hex));
  syncThemeColor();
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  syncThemeColor();
}

function update(next: Appearance) {
  const before = current;
  current = next;
  if (next.brand !== before.brand) applyBrand(next.brand);
  if (next.theme !== before.theme) applyTheme(next.theme);
  listeners.forEach((fn) => fn());
}

export function setBrand(hex: string) {
  const c = parseHex(hex);
  if (!c) return;
  const brand = toHex(c);
  writeStorage(BRAND_KEY, brand);
  update({ ...current, brand, brandId: BRAND_PRESETS.find((p) => p.hex === brand)?.id ?? 'custom' });
}

export function setTheme(theme: Theme) {
  writeStorage(THEME_KEY, theme);
  update({ ...current, theme });
}

/**
 * Einmal beim Start, vor dem ersten Rendern. Danach folgen alle offenen Tabs
 * einer Aenderung in einem anderen Tab -- wer in der Startseite die Farbe
 * wechselt, sieht sie sofort auch in der danebenliegenden Regie.
 */
export function initAppearance() {
  applyBrand(current.brand);
  applyTheme(current.theme);
  window.addEventListener('storage', (e) => {
    if (e.key === BRAND_KEY || e.key === THEME_KEY) update(readAppearance());
  });
  darkQuery()?.addEventListener('change', () => {
    if (!readStorage(THEME_KEY)) update(readAppearance());
  });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useAppearance(): Appearance {
  return useSyncExternalStore(subscribe, () => current);
}
